import React, { useRef } from "react";
import Reveal from "./Reveal";
import { Play, ChevronLeft, ChevronRight } from "lucide-react";

const reels = [
  {
    title: "Hook in 1.2s",

    videoSrc: "/videos/reel1.mp4",
    link: "https://www.instagram.com/reel/DVscbTKEsWZ/?igsh=MWVseXV5cjkxdHZ0dA==",
  },
  {
    title: "Story-led ad",

    videoSrc: "/videos/reel2.mp4",
    link: "https://www.instagram.com/reel/DAVwlm7qhWb/?igsh=N3V3aGtreDg1bThy",
  },
  {
    title: "Creator unbox",

    videoSrc: "/videos/reel3.mp4",
    link: "https://www.instagram.com/reel/DFPtvPWznuw/?igsh=MWJzcmIwc2wwb2psZw%3D%3D",
  },
  {
    title: "Brand film",

    videoSrc: "/videos/reel4.mp4",
    link: "https://www.instagram.com/reel/DTLDj9YksA1/?igsh=MTcwMzMxZWU4dThvcQ==",
  },
  {
    title: "Creative Releases",

    videoSrc: "/videos/reel5.mp4",
    link: "https://www.instagram.com/satya.pandari/reel/DUQkWJRkej8/",
  },
  {
    title: "Concept Shoots",

    videoSrc: "/videos/reel6.mp4",
    link: "https://www.instagram.com/reel/DU8ggULEoT3/?igsh=bDU5bmpucHB4ajVy",
  },
  {
    title: "Moments in Motion",

    videoSrc: "/videos/reel7.mp4",
    link: "https://www.instagram.com/reel/DS8B2XIEsTp/?igsh=MTdtbGwxNWE0YW0xdg%3D%3D",
  },
];

const stats = [
  { k: "92M+", v: "Combined reach" },
  { k: "3.1s", v: "Avg. avg watch time" },
  { k: "4.8×", v: "Engagement vs. baseline" },
  { k: "180+", v: "Reels shipped" },
];

const Reels = () => {
  // 👇 ref goes on the overflow-x-auto wrapper (the actual scrolling element)
  const scrollRef = useRef(null);

  const scroll = (dir) => {
    const container = scrollRef.current;
    if (!container) return;
    // grab the first card's width + gap so we scroll exactly one card
    const firstCard = container.querySelector("[data-reel-card]");
    const cardWidth = firstCard ? firstCard.offsetWidth : 420;
    const gap = 28; // matches md:gap-7 (28px)
    container.scrollBy({
      left: dir === "next" ? cardWidth + gap : -(cardWidth + gap),
      behavior: "smooth",
    });
  };

  return (
    <section
      id="reels"
      data-testid="reels-section"
      className="bg-white pt-20 md:pt-28 pb-20 md:pb-28 border-t border-black/10"
    >
      <div className="px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-12 gap-6 items-end mb-10 md:mb-14">
          {/* ── Left: heading ── */}
          <div className="col-span-12 md:col-span-7">
            <p className="flex items-center gap-2 text-[12px] tracking-[0.28em] uppercase font-bold text-[#E60000] mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-[#E60000]" />
              Reels & Visual
            </p>
            <Reveal>
              <h2
                data-testid="reels-heading"
                className="font-display font-black uppercase text-[40px] sm:text-[56px] md:text-[76px] leading-[0.95] tracking-tighter text-black"
              >
                Content that stops,
                <br />
                engages, and converts<span className="text-[#E60000]">.</span>
              </h2>
            </Reveal>
          </div>

          {/* ── Right: description + nav buttons ── */}
          <div className="col-span-12 md:col-span-5 flex flex-col gap-6">
            {/* ── Nav buttons — right-aligned on md+, left on mobile ── */}
            <Reveal delay={200}>
              <div className="flex items-center gap-3 md:justify-end">
                <button
                  onClick={() => scroll("prev")}
                  aria-label="Previous reel"
                  className="
                                        w-11 h-11 rounded-full
                                        border border-black/25
                                        flex items-center justify-center
                                        text-black
                                        hover:bg-black hover:text-white hover:border-black
                                        transition-all duration-200
                                    "
                >
                  <ChevronLeft size={18} strokeWidth={2.5} />
                </button>

                <button
                  onClick={() => scroll("next")}
                  aria-label="Next reel"
                  className="
                                        w-11 h-11 rounded-full
                                        bg-black text-white
                                        flex items-center justify-center
                                        hover:bg-[#E60000]
                                        transition-all duration-200
                                    "
                >
                  <ChevronRight size={18} strokeWidth={2.5} />
                </button>
              </div>
            </Reveal>
          </div>
        </div>
      </div>

      {/* ── Horizontal scroll of 9:16 reels — ref is here ── */}
      <div
        ref={scrollRef}
        className="overflow-x-auto no-scrollbar px-6 md:px-12 lg:px-16"
      >
        <div className="flex gap-5 md:gap-7 pb-3 snap-x snap-mandatory">
          {reels.map((r, i) => (
            <Reveal
              key={`${r.title}-${i}`}
              delay={i * 80}
              data-reel-card // 👈 used to measure card width in scroll()
              className="
                                relative shrink-0
                                w-[76vw]
                                sm:w-[340px]
                                md:w-[380px]
                                lg:w-[420px]
                                xl:w-[440px]
                                max-w-[440px]
                                aspect-[9/16]
                                max-h-[78vh]
                                bg-black overflow-hidden group snap-start
                            "
            >
              <a
                href={r.link}
                target="_blank"
                rel="noopener noreferrer"
                className="absolute inset-0 z-30"
                aria-label={`Open ${r.title} on Instagram`}
              />

              {r.videoSrc ? (
                <video
                  src={r.videoSrc}
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : (
                <img
                  src={r.img}
                  alt={r.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover grayscale-img"
                />
              )}

              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/15 to-transparent" />

              <div className="absolute top-4 left-4 right-4 flex items-center justify-between text-white z-20 pointer-events-none">
                <span className="text-[10px] md:text-[11px] tracking-[0.22em] uppercase font-bold bg-black/60 backdrop-blur px-2.5 py-1">
                  {r.meta}
                </span>
              </div>

              <div className="absolute bottom-4 left-4 right-4 text-white z-20 pointer-events-none">
                <h3 className="font-display font-bold uppercase tracking-tight text-xl md:text-2xl">
                  {r.title}
                </h3>
                <div className="flex items-center gap-2 text-[10px] md:text-[11px] tracking-[0.22em] uppercase mt-1 opacity-80">
                  <span className="w-1 h-1 rounded-full bg-[#E60000]" />
                  Dot Media · Original
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      {/* ── Stat strip ── */}
      <div className="px-6 md:px-12 lg:px-16 mt-12 md:mt-16">
        <div className="grid grid-cols-2 md:grid-cols-4 border-y border-black/15 divide-x divide-black/15">
          {stats.map((s) => (
            <div
              key={s.k}
              className="px-5 py-6 md:py-8"
              data-testid={`reels-stat-${s.k}`}
            >
              <div className="font-display font-black text-3xl md:text-5xl tracking-tighter text-black">
                {s.k}
                <span className="text-[#E60000]">.</span>
              </div>
              <p className="mt-2 text-[11px] tracking-[0.22em] uppercase font-bold text-black/60">
                {s.v}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Reels;
